import { Datepicker } from '@ibsys/kit';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { AttributeValueField, getAttributeValueKind } from '@/features/attribute-filters';
import { cn, type TimeIntervalFormat } from '@/shared/lib';
import { RemovableField } from '@/shared/ui';
import {
  CALL_TIME_PROPERTY,
  CLIENT_PROPERTY,
  EMPLOYEE_PROPERTY,
  type RecordPropertyValueName,
  type RecordValueSetForm,
} from '../model/recordValueSet';
import { RecordClientField } from './RecordClientField';
import { RecordEmployeeField } from './RecordEmployeeField';

type Props = {
  /** Имя поля значения в форме набора */
  name: RecordPropertyValueName;
  /** Ключ свойства */
  property: string;
  /** Название свойства */
  label: string;
  /** Атрибут свойства; не задан — свойство не атрибут либо справочник атрибутов ещё не пришёл */
  attribute?: ConversationAttributeItem;
  /** Действующий формат показа интервала времени — от него зависит поле длительности */
  timeDisplayFormat: TimeIntervalFormat;
  disabled: boolean;
  onRemove: () => void;
};

/** Ячейка набора значений: название свойства с удалением из набора над полем его значения */
export const RecordPropertyCell: FC<Props> = ({
  name,
  property,
  label,
  attribute,
  timeDisplayFormat,
  disabled,
  onRemove,
}) => {
  const { t } = useTranslation(['records']);
  const { control } = useFormContext<RecordValueSetForm>();
  const kind = attribute ? getAttributeValueKind(attribute, timeDisplayFormat) : null;

  return (
    <RemovableField
      label={label}
      removeLabel={t('records:valueSet.remove', { name: label })}
      onRemove={onRemove}
      disabled={disabled}
      className={cn('w-full', property === CLIENT_PROPERTY ? 'sm:w-auto' : 'sm:w-72')}
    >
      {property === EMPLOYEE_PROPERTY && <RecordEmployeeField name={name} disabled={disabled} />}
      {property === CLIENT_PROPERTY && <RecordClientField name={name} disabled={disabled} />}
      {property === CALL_TIME_PROPERTY && (
        <Datepicker control={control} name={name} showTimeInputs disabled={disabled} />
      )}
      {attribute && kind && <AttributeValueField attribute={attribute} kind={kind} name={name} disabled={disabled} />}
    </RemovableField>
  );
};
